<template>
	<div class="single-date">
		<div class="bound">
			<DatePicker.Root
				selectionMode="single"
				:value="datePickerValue"
				@valueChange="onDateChange"
				:startOfWeek="startOfWeek"
				:locale="locale"
				minView="day"
				maxView="day"
				inline
			>
				<DatePicker.Control>
					<div ref="inputRef" class="date-input" :class="{ disabled }" @mousedown="onInputMousedown">
						<input
							ref="yearRef"
							type="text"
							inputmode="numeric"
							class="date-segment year"
							:placeholder="yearPlaceholder"
							maxlength="4"
							:value="yearSeg"
							:style="{ width: yearWidth + 'px' }"
							@input="onYearInput"
							@blur="commitSegments"
							@keydown="onYearKeydown"
							:disabled="disabled"
						/>
						<span class="date-sep">-</span>
						<input
							ref="monthRef"
							type="text"
							inputmode="numeric"
							class="date-segment"
							placeholder="MM"
							maxlength="2"
							:value="monthSeg"
							:style="{ width: monthWidth + 'px' }"
							@input="onMonthInput"
							@blur="onMonthBlur"
							@keydown="onMonthKeydown"
							:disabled="disabled"
						/>
						<span class="date-sep">-</span>
						<input
							ref="dayRef"
							type="text"
							inputmode="numeric"
							class="date-segment"
							:placeholder="dayPlaceholder"
							maxlength="2"
							:value="daySeg"
							:style="{ width: dayWidth + 'px' }"
							@input="onDayInput"
							@blur="onDayBlur"
							@keydown="onDayKeydown"
							:disabled="disabled"
						/>
					</div>
					<div class="date-actions">
						<button v-if="hasValue && !disabled" type="button" class="date-clear" @click="onClear">×</button>
						<button ref="triggerRef" type="button" class="date-trigger" :disabled="disabled" @click="onTriggerClick">
							<AppIcon :name="IconName.Calendar" class="calendar" />
						</button>
					</div>
				</DatePicker.Control>

				<Teleport to="body">
					<div
						v-if="isOpen"
						ref="flyoutRef"
						class="date-custom-panel"
						:style="{ top: flyoutStyle.top, left: flyoutStyle.left }"
					>
						<div class="date-content">
							<DatePicker.View view="day">
								<DatePicker.Context v-slot="api">
									<div class="date-view-control">
										<DatePicker.MonthSelect class="date-select" />
										<DatePicker.YearSelect class="date-select" />
									</div>

									<DatePicker.Table class="date-table">
										<DatePicker.TableHead>
											<DatePicker.TableRow>
												<DatePicker.TableHeader v-for="(weekDay, id) in api.weekDays" :key="id" class="date-header">
													{{ weekDay.narrow }}
												</DatePicker.TableHeader>
											</DatePicker.TableRow>
										</DatePicker.TableHead>
										<DatePicker.TableBody>
											<DatePicker.TableRow v-for="(week, w) in api.weeks" :key="w">
												<DatePicker.TableCell v-for="(day, d) in week" :key="d" :value="day">
													<DatePicker.TableCellTrigger class="date-day" @click="onDayClick(day)">
														{{ day.day }}
													</DatePicker.TableCellTrigger>
												</DatePicker.TableCell>
											</DatePicker.TableRow>
										</DatePicker.TableBody>
									</DatePicker.Table>
								</DatePicker.Context>
							</DatePicker.View>
						</div>
					</div>
				</Teleport>
			</DatePicker.Root>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue"
import { DatePicker } from "@ark-ui/vue"
import { onClickOutside } from "@vueuse/core"
import { parseDate as parseCalendarDate, type DateValue } from "@internationalized/date"
import { formatDateToISO, parseISODate, isValidDateString, combineDateSegments } from "@/utils/date"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"

const props = withDefaults(
	defineProps<{
		modelValue?: Date | null
		locale?: "fr-FR" | "en-US"
		startOfWeek?: 0 | 1
		disabled?: boolean
	}>(),
	{
		modelValue: null,
		locale: "fr-FR",
		startOfWeek: 1,
		disabled: false,
	}
)

const emit = defineEmits<{
	"update:modelValue": [Date | null]
	change: [Date | null]
}>()

const yearPlaceholder = computed(() => (props.locale === "fr-FR" ? "AAAA" : "YYYY"))
const dayPlaceholder = computed(() => (props.locale === "fr-FR" ? "JJ" : "DD"))

const yearSeg = ref("")
const monthSeg = ref("")
const daySeg = ref("")

function setSegmentsFromDate(date: Date | null) {
	if (!date) {
		yearSeg.value = ""
		monthSeg.value = ""
		daySeg.value = ""
		return
	}

	const [year, month, day] = formatDateToISO(date).split("-")
	yearSeg.value = year
	monthSeg.value = month
	daySeg.value = day
}

function segmentsToIsoIfComplete(): string | null {
	if (!yearSeg.value && !monthSeg.value && !daySeg.value) return null
	if (yearSeg.value.length < 4 || monthSeg.value.length < 2 || daySeg.value.length < 2) return null
	return `${yearSeg.value}-${monthSeg.value}-${daySeg.value}`
}

const datePickerValue = computed<DateValue[]>(() => {
	const date = combineDateSegments(yearSeg.value, monthSeg.value, daySeg.value)
	return date ? [parseCalendarDate(formatDateToISO(date))] : []
})

setSegmentsFromDate(props.modelValue)

watch(
	() => props.modelValue,
	newDate => {
		// skip re-deriving segments when this is just our own emit echoing back, which would clobber an in-progress edit
		const iso = newDate ? formatDateToISO(newDate) : null
		if (iso === segmentsToIsoIfComplete()) return

		setSegmentsFromDate(newDate)
	}
)

// undefined means "incomplete or invalid, still typing" as opposed to null which means "cleared"
function segmentsToDate(): Date | null | undefined {
	if (!yearSeg.value && !monthSeg.value && !daySeg.value) return null
	return combineDateSegments(yearSeg.value, monthSeg.value, daySeg.value) ?? undefined
}

const hasValue = computed(() => Boolean(yearSeg.value || monthSeg.value || daySeg.value))

// when the field is empty, clicking anywhere in it should start at the year segment
function onInputMousedown(e: MouseEvent) {
	if (hasValue.value) return
	e.preventDefault()
	yearRef.value?.focus()
}

function onClear() {
	setSegmentsFromDate(null)
	emitAll(null)
	yearRef.value?.focus()
}

function commitSegments() {
	const result = segmentsToDate()
	if (result === undefined) return

	emitAll(result)
}

// moves focus to an adjacent segment when the caret is at the edge of the current one
function handleArrowNav(e: KeyboardEvent, prev?: HTMLInputElement | null, next?: HTMLInputElement | null) {
	const el = e.target as HTMLInputElement
	if (e.key === "ArrowLeft" && el.selectionStart === 0 && prev) {
		e.preventDefault()
		prev.focus()
		prev.setSelectionRange(prev.value.length, prev.value.length)
	} else if (e.key === "ArrowRight" && el.selectionStart === el.value.length && next) {
		e.preventDefault()
		next.focus()
		next.setSelectionRange(0, 0)
	}
}

function onYearInput(e: Event) {
	const el = e.target as HTMLInputElement
	const digits = el.value.replace(/\D/g, "").slice(0, 4)
	yearSeg.value = digits
	el.value = digits
	if (digits.length === 4) monthRef.value?.focus()
	commitSegments()
}

function onYearKeydown(e: KeyboardEvent) {
	handleArrowNav(e, undefined, monthRef.value)
}

function onMonthInput(e: Event) {
	const el = e.target as HTMLInputElement
	const digits = el.value.replace(/\D/g, "").slice(0, 2)
	monthSeg.value = digits
	el.value = digits
	if (digits.length === 2 || (digits.length === 1 && Number(digits) > 1)) {
		monthSeg.value = digits.padStart(2, "0")
		dayRef.value?.focus()
		dayRef.value?.select()
	}
	commitSegments()
}

function onMonthBlur() {
	if (monthSeg.value.length === 1) monthSeg.value = monthSeg.value.padStart(2, "0")
	commitSegments()
}

function onMonthKeydown(e: KeyboardEvent) {
	if (e.key === "Backspace" && monthSeg.value === "") {
		yearRef.value?.focus()
		return
	}
	handleArrowNav(e, yearRef.value, dayRef.value)
}

function onDayInput(e: Event) {
	const el = e.target as HTMLInputElement
	const digits = el.value.replace(/\D/g, "").slice(0, 2)
	daySeg.value = digits
	el.value = digits
	if (digits.length === 2 || (digits.length === 1 && Number(digits) > 3)) {
		daySeg.value = digits.padStart(2, "0")
	}
	commitSegments()
}

function onDayBlur() {
	if (daySeg.value.length === 1) daySeg.value = daySeg.value.padStart(2, "0")
	commitSegments()
}

function onDayKeydown(e: KeyboardEvent) {
	if (e.key === "Backspace" && daySeg.value === "") {
		monthRef.value?.focus()
		return
	}
	handleArrowNav(e, monthRef.value, undefined)
}

function onDayClick(day: unknown) {
	if (!day || typeof day !== "object" || !("year" in day) || !("month" in day) || !("day" in day)) {
		return
	}

	const dateObj = day as { year: number; month: number; day: number }
	const clickedDate = `${dateObj.year}-${String(dateObj.month).padStart(2, "0")}-${String(dateObj.day).padStart(2, "0")}`

	setSegmentsFromDate(parseISODate(clickedDate))
	emitAll(parseISODate(clickedDate))
	isOpen.value = false
}

interface DateChangeEvent {
	value: DateValue[]
	valueAsString: string[]
	view: string
}

function onDateChange(eventData: DateChangeEvent) {
	const selectedDate = eventData?.valueAsString?.[0]

	if (!selectedDate || !isValidDateString(selectedDate)) return

	setSegmentsFromDate(parseISODate(selectedDate))
	emitAll(parseISODate(selectedDate))
	isOpen.value = false
}

function emitAll(d: Date | null) {
	emit("update:modelValue", d)
	emit("change", d)
}

// the calendar opens as a flyout positioned relative to the trigger, matching ArkDateRangePicker's custom-range panel
const isOpen = ref(false)
const triggerRef = ref<HTMLButtonElement | null>(null)
const inputRef = ref<HTMLDivElement | null>(null)
const yearRef = ref<HTMLInputElement | null>(null)
const monthRef = ref<HTMLInputElement | null>(null)
const dayRef = ref<HTMLInputElement | null>(null)
const flyoutRef = ref<HTMLElement | null>(null)
const flyoutStyle = ref({ top: "0px", left: "0px" })

// segments auto-size to their current content (typed digits or placeholder), since inputs don't support width: fit-content
const MEASURE_CANVAS = document.createElement("canvas").getContext("2d")
const SEGMENT_WIDTH_BUFFER = 3
const yearWidth = ref(32)
const monthWidth = ref(22)
const dayWidth = ref(22)

function measureTextWidth(text: string, el: HTMLInputElement | null): number {
	if (!el || !MEASURE_CANVAS) return 0
	const style = getComputedStyle(el)
	MEASURE_CANVAS.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
	return MEASURE_CANVAS.measureText(text).width
}

function updateSegmentWidths() {
	yearWidth.value =
		Math.ceil(measureTextWidth(yearSeg.value || yearPlaceholder.value, yearRef.value)) + SEGMENT_WIDTH_BUFFER
	monthWidth.value = Math.ceil(measureTextWidth(monthSeg.value || "MM", monthRef.value)) + SEGMENT_WIDTH_BUFFER
	dayWidth.value =
		Math.ceil(measureTextWidth(daySeg.value || dayPlaceholder.value, dayRef.value)) + SEGMENT_WIDTH_BUFFER
}

onMounted(updateSegmentWidths)
watch([yearSeg, monthSeg, daySeg, yearPlaceholder, dayPlaceholder], updateSegmentWidths)

// matches the width of a single calendar column inside ArkDateRangePicker's custom-range flyout
const FLYOUT_WIDTH = 234
const FLYOUT_MARGIN = 8

// positioned above the trigger, right edge aligned with the input's right edge
function updateFlyoutPosition() {
	const trigger = triggerRef.value
	const input = inputRef.value
	if (!trigger || !input) return

	const triggerRect = trigger.getBoundingClientRect()
	const inputRect = input.getBoundingClientRect()
	const flyoutWidth = Math.min(FLYOUT_WIDTH, window.innerWidth - FLYOUT_MARGIN * 2)
	const left = Math.max(
		FLYOUT_MARGIN,
		Math.min(inputRect.right - flyoutWidth, window.innerWidth - flyoutWidth - FLYOUT_MARGIN)
	)

	const flyoutHeight = flyoutRef.value?.offsetHeight ?? 360
	const fitsAbove = triggerRect.top - FLYOUT_MARGIN - flyoutHeight >= 0
	const top = fitsAbove ? triggerRect.top - flyoutHeight - FLYOUT_MARGIN : triggerRect.bottom + FLYOUT_MARGIN

	flyoutStyle.value = { top: `${top}px`, left: `${left}px` }
}

function handleReposition() {
	if (isOpen.value) updateFlyoutPosition()
}

onMounted(() => {
	window.addEventListener("resize", handleReposition)
	window.addEventListener("scroll", handleReposition, true)
})

onUnmounted(() => {
	window.removeEventListener("resize", handleReposition)
	window.removeEventListener("scroll", handleReposition, true)
})

onClickOutside(triggerRef, () => (isOpen.value = false), { ignore: [flyoutRef] })

function onTriggerClick() {
	isOpen.value = !isOpen.value
	if (isOpen.value) nextTick(updateFlyoutPosition)
}
</script>

<style scoped>
.single-date {
	width: 100%;

	.label {
		display: block;
		font-size: var(--font-size-small);
		color: var(--neutral-600);
		margin-bottom: var(--spacing-xs);
	}

	[data-scope="date-picker"][data-part="control"] {
		position: relative;
		display: flex;
		align-items: center;
	}

	.date-input {
		display: flex;
		align-items: center;
		gap: 0;
		min-width: 100%;
		padding: var(--spacing-xs) var(--spacing-sm);
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-base);
		font-size: var(--font-size-regular);
		height: var(--spacing-xl);
		background-color: var(--neutral-0);

		&:focus-within {
			border-color: var(--primary-green);
			box-shadow: var(--shadow-base);
		}

		&.disabled {
			opacity: 0.6;
		}
	}

	.date-segment {
		border: none;
		outline: none;
		padding: 0;
		font-size: var(--font-size-regular);
		font-family: inherit;
		color: var(--neutral-900);
		background: transparent;
		text-align: center;
		cursor: text;

		&::placeholder {
			color: var(--neutral-400);
		}
	}

	.date-sep {
		color: var(--neutral-400);
		font-size: var(--font-size-regular);
		margin: 0 1px;
	}

	.date-actions {
		position: absolute;
		right: 4px;
		top: 50%;
		transform: translateY(-50%);
		display: flex;
		align-items: center;
		gap: var(--spacing-xxs);
	}

	.date-trigger {
		background: none;
		border: none;
		cursor: pointer;
		padding: var(--spacing-padding-xxs) var(--spacing-padding-xs);
		border-radius: var(--border-radius-sm);
	}

	.date-clear {
		display: flex;
		align-items: center;
		justify-content: center;
		width: var(--spacing-md);
		height: var(--spacing-md);
		border: none;
		background: var(--neutral-200);
		border-radius: 50%;
		cursor: pointer;
		color: var(--neutral-900);
		font-size: var(--font-size-small);
		line-height: 1;
		transition: background-color 0.2s ease;

		&:hover:not(:disabled) {
			background: var(--neutral-400);
		}
	}

	.calendar {
		width: 16px;
		height: 16px;
		color: var(--neutral-600);

		&:hover {
			color: var(--blue-dark);
		}
	}
}

.date-custom-panel {
	position: fixed;
	z-index: 1200;
	width: min(234px, calc(100vw - var(--spacing-md) * 2));
	max-height: calc(100vh - var(--spacing-md) * 2);
	overflow-y: auto;
	background: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	box-shadow: var(--shadow-lg);
	padding: var(--spacing-sm);

	.date-content {
		background: var(--neutral-0);
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-base);
		padding: var(--spacing-sm) var(--spacing-xs);
	}

	.date-view-control {
		display: flex;
		justify-content: center;
		gap: var(--spacing-xxs);
		margin-bottom: var(--spacing-xs);

		.date-select {
			padding: var(--spacing-xxs) var(--spacing-xs);
			border: none;
			border-radius: var(--border-radius-sm);
			font-size: var(--font-size-xs);
			font-weight: var(--font-weight-semibold);
			background: var(--neutral-0);
			color: var(--blue-dark);
			cursor: pointer;
			text-align: center;
			text-align-last: center;
			text-transform: capitalize;

			&:hover {
				background: var(--neutral-200);
			}
		}
	}

	.date-table {
		width: 100%;
		border-collapse: separate;
		border-spacing: var(--spacing-xxs);
		table-layout: fixed;
		text-align: center;

		.date-header {
			padding: var(--spacing-xs) 0;
			text-align: center;
			font-size: var(--font-size-xs);
			color: var(--neutral-600);
			font-weight: var(--font-weight-medium);
		}

		.date-day {
			width: 100%;
			aspect-ratio: 1;
			border: none;
			background: none;
			border-radius: var(--border-radius-sm);
			cursor: pointer;
			font-size: var(--font-size-xs);
			display: inline-flex;
			align-items: center;
			justify-content: center;
			color: var(--neutral-600);

			&:hover {
				background: var(--neutral-200);
			}

			&[data-outside-range] {
				color: var(--neutral-200);
				cursor: default;

				&:hover {
					background: none;
				}
			}

			&[data-selected] {
				background: var(--primary-green);
				color: var(--neutral-0);
			}

			&[data-today] {
				position: relative;

				&::after {
					content: "";
					position: absolute;
					bottom: 1px;
					left: 50%;
					transform: translateX(-50%);
					width: 3px;
					height: 3px;
					background: var(--primary-green);
					border-radius: 50%;
				}
			}
		}
	}
}
</style>
